import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useCart } from '@/context/CartContext';
import { formatINR } from '@/lib/money';
import { getProductById } from '@/lib/data';
import { PlaceholderImage } from './PlaceholderImage';

const RectTile = ({ item, selected, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    data-testid={`hamper-pick-${item.id}`}
    className="relative text-left rounded-2xl overflow-hidden border-2 transition-all"
    style={{
      borderColor: selected ? 'var(--cw-blue)' : 'var(--cw-border)',
      backgroundColor: selected ? 'rgba(153, 193, 241, 0.12)' : '#fff',
    }}
  >
    <div className="relative w-full aspect-[16/10] bg-[#f4f6f8] overflow-hidden">
      {item.placeholder ? (
        <PlaceholderImage
          label={item.name}
          bg={item.placeholderColor}
          ink={item.placeholderInk}
          subLabel=""
        />
      ) : (
        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
      )}
      {selected && (
        <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[var(--cw-blue)] flex items-center justify-center shadow-md">
          <Check className="w-4 h-4 text-[var(--cw-text)]" />
        </span>
      )}
    </div>
    <div className="p-3">
      <div className="font-heading text-sm font-medium leading-tight">{item.name}</div>
      <div className="text-xs text-[var(--cw-muted)] mt-0.5">{formatINR(item.price)}</div>
    </div>
  </button>
);

export const CustomizeHamperDialog = ({ open, onOpenChange, product }) => {
  const { addItem } = useCart();
  const choicesFrom = (product?.choicesFrom || []).map(getProductById).filter(Boolean);
  const target = product?.chooseCount || 4;
  const [picked, setPicked] = useState([]);

  useEffect(() => {
    if (open && product) {
      setPicked(product.contains ? [...product.contains].slice(0, target) : []);
    }
  }, [open, product, target]);

  const toggle = (id) => {
    setPicked((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= target) return cur; // block extra selections
      return [...cur, id];
    });
  };

  const canConfirm = picked.length === target;

  const confirm = () => {
    const names = picked.map((id) => getProductById(id)?.name).filter(Boolean);
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      customNames: names,
    });
    onOpenChange(false);
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl bg-white border-[var(--cw-border)] rounded-3xl p-0 overflow-hidden"
        data-testid="hamper-customise-dialog"
      >
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-[var(--cw-border)]">
          <DialogTitle className="font-heading text-2xl font-medium">
            Build your gift hamper
          </DialogTitle>
          <DialogDescription className="text-[var(--cw-muted)]">
            Pick any {target} soaps · {picked.length}/{target} selected
          </DialogDescription>
        </DialogHeader>

        <div className="px-8 py-6 max-h-[65vh] overflow-y-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {choicesFrom.map((it) => (
              <RectTile
                key={it.id}
                item={it}
                selected={picked.includes(it.id)}
                onToggle={() => toggle(it.id)}
              />
            ))}
          </div>
        </div>

        <div className="px-8 py-5 border-t border-[var(--cw-border)] bg-[var(--cw-bg)] flex items-center justify-between gap-3">
          <div className="text-sm text-[var(--cw-muted)]">
            Hamper price · <span className="font-heading text-base text-[var(--cw-text)]">{formatINR(product.price)}</span>
          </div>
          <button
            type="button"
            onClick={confirm}
            disabled={!canConfirm}
            data-testid="hamper-confirm-btn"
            className="cw-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add hamper to cart
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
